import React, { useState } from 'react';
import './Postings.css';
import { MdFilterList, MdAdd, MdDelete, MdEdit, MdVisibility, MdClose, MdArrowDropDown } from "react-icons/md";
import { MdPostAdd } from "react-icons/md";
import { FaChevronLeft, FaChevronRight, FaFileInvoiceDollar, FaListUl, FaSearch } from "react-icons/fa";

// Today's date as YYYY-MM-DD (no hardcoded dates)
const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Empty form defaults (no hardcoded names / amounts)
const getEmptyPaymentForm = () => ({
  account: '',
  amount: '',
  paymentDate: getTodayStr(),
  referenceNo: '',
  paymentMethod: ''
});

const getEmptyInvoiceForm = () => ({
  account: '',
  invoiceDate: getTodayStr(),
  dueDate: '',
  note: '',
  subtotal: '',
  taxes: '',
  total: ''
});

const getEmptyCreditForm = () => ({
  account: '',
  amount: '',
  creditDate: getTodayStr(),
  referenceNo: '',
  description: '',
  applied: '',
  unapplied: ''
});

const Posting = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedPostings, setSelectedPostings] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Payment form state
  const [paymentForm, setPaymentForm] = useState(getEmptyPaymentForm());

  // Invoice form state
  const [invoiceForm, setInvoiceForm] = useState(getEmptyInvoiceForm());

  // Credit form state
  const [creditForm, setCreditForm] = useState(getEmptyCreditForm());

  // ✅ No hardcoded data - load real data from backend here (setPostings)
  const [postings] = useState([]);

  // Get unique accounts for dropdowns (from real data)
  const uniqueAccounts = [...new Set(postings.map(p => p.account))];

  // Filter postings based on search and filters
  const filteredPostings = postings.filter(posting => {
    const matchesSearch =
      posting.account.toLowerCase().includes(searchTerm.toLowerCase()) ||
      posting.reference.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === '' || posting.type === filterType;

    const matchesAccount = filterAccount === '' || posting.account === filterAccount;

    const matchesDate = () => {
      if (!startDate && !endDate) return true;
      const postDate = new Date(posting.postDate);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && end) {
        return postDate >= start && postDate <= end;
      }
      if (start) {
        return postDate >= start;
      }
      if (end) {
        return postDate <= end;
      }
      return true;
    };

    return matchesSearch && matchesType && matchesAccount && matchesDate();
  });

  // Calculate pagination
  const totalPostings = filteredPostings.length;
  const totalPages = Math.ceil(totalPostings / rowsPerPage);
  const indexOfLastPosting = currentPage * rowsPerPage;
  const indexOfFirstPosting = indexOfLastPosting - rowsPerPage;
  const currentPostings = filteredPostings.slice(indexOfFirstPosting, indexOfLastPosting);

  // Previous page
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Next page
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Select all postings
  const handleSelectAll = () => {
    if (selectedPostings.length === currentPostings.length) {
      setSelectedPostings([]);
    } else {
      setSelectedPostings(currentPostings.map(posting => posting.id));
    }
  };

  // Select single posting
  const handleSelectPosting = (id) => {
    if (selectedPostings.includes(id)) {
      setSelectedPostings(selectedPostings.filter(pid => pid !== id));
    } else {
      setSelectedPostings([...selectedPostings, id]);
    }
  };

  // Handle edit
  const handleEdit = (id) => {
    alert(`Edit posting with ID: ${id}`);
  };

  // Handle delete
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this posting?')) {
      alert(`Posting with ID: ${id} deleted`);
    }
  };

  // Handle view
  const handleView = (id) => {
    alert(`View posting with ID: ${id}`);
  };

  // Handle create new entries
  const handleCreateEntry = (type) => {
    setShowCreateDropdown(false);
    if (type === 'Payment') {
      setShowPaymentModal(true);
    } else if (type === 'Invoice') {
      setShowInvoiceModal(true);
    } else if (type === 'Credit') {
      setShowCreditModal(true);
    }
  };

  // Handle payment form changes
  const handlePaymentFormChange = (field, value) => {
    setPaymentForm({
      ...paymentForm,
      [field]: value
    });
  };

  // Handle save payment
  const handleSavePayment = () => {
    alert(`Payment saved:\nAccount: ${paymentForm.account}\nAmount: £${paymentForm.amount}\nDate: ${paymentForm.paymentDate}\nReference: ${paymentForm.referenceNo || '—'}\nMethod: ${paymentForm.paymentMethod || 'Not selected'}`);
    setShowPaymentModal(false);
    setPaymentForm(getEmptyPaymentForm());
  };

  // Handle cancel payment
  const handleCancelPayment = () => {
    setShowPaymentModal(false);
  };

  // Handle invoice form changes
  const handleInvoiceFormChange = (field, value) => {
    setInvoiceForm({
      ...invoiceForm,
      [field]: value
    });
  };

  // Calculate totals for invoice
  const calculateTotals = () => {
    const subtotal = parseFloat(invoiceForm.subtotal) || 0;
    const taxes = parseFloat(invoiceForm.taxes) || 0;
    const total = subtotal + taxes;
    return { subtotal, taxes, total };
  };

  // Handle save invoice
  const handleSaveInvoice = () => {
    const { subtotal, taxes, total } = calculateTotals();
    alert(`Invoice saved:\nAccount: ${invoiceForm.account}\nInvoice Date: ${invoiceForm.invoiceDate}\nDue Date: ${invoiceForm.dueDate || 'Not set'}\nNote: ${invoiceForm.note}\nSubtotal: £${subtotal.toFixed(2)}\nTaxes: £${taxes.toFixed(2)}\nTotal: £${total.toFixed(2)}`);
    setShowInvoiceModal(false);
    setInvoiceForm(getEmptyInvoiceForm());
  };

  // Handle cancel invoice
  const handleCancelInvoice = () => {
    setShowInvoiceModal(false);
  };

  // Handle credit form changes
  const handleCreditFormChange = (field, value) => {
    setCreditForm({
      ...creditForm,
      [field]: value
    });
  };

  // Calculate unapplied amount for credit
  const calculateUnapplied = () => {
    const amount = parseFloat(creditForm.amount) || 0;
    const applied = parseFloat(creditForm.applied) || 0;
    const unapplied = amount - applied;
    return unapplied;
  };

  // Handle save credit
  const handleSaveCredit = () => {
    const unapplied = calculateUnapplied();
    const applied = parseFloat(creditForm.applied) || 0;
    alert(`Credit saved:\nAccount: ${creditForm.account}\nAmount: £${creditForm.amount}\nCredit Date: ${creditForm.creditDate}\nReference: ${creditForm.referenceNo || '—'}\nDescription: ${creditForm.description}\nApplied: £${applied.toFixed(2)}\nUnapplied: £${unapplied.toFixed(2)}`);
    setShowCreditModal(false);
    setCreditForm(getEmptyCreditForm());
  };

  // Handle cancel credit
  const handleCancelCredit = () => {
    setShowCreditModal(false);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilterType('');
    setFilterAccount('');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  // Open filter modal
  const openFilterModal = () => {
    setShowFilterModal(true);
  };

  // Close filter modal
  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  // Toggle create dropdown
  const toggleCreateDropdown = () => {
    setShowCreateDropdown(!showCreateDropdown);
  };

  // Close create dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCreateDropdown && !event.target.closest('.create-dropdown')) {
        setShowCreateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateDropdown]);

  const { subtotal, taxes, total } = calculateTotals();
  const unappliedAmount = calculateUnapplied();
  const appliedAmount = parseFloat(creditForm.applied) || 0;

  return (
    <div className="postings-page">
      {/* Header: title + badge (left) | controls (right) */}
      <div className="postings-header">
        <div className="postings-header-left">
          <h1>
            <MdPostAdd  className="postings-title-icon" /> Posting Entries
          </h1>

        </div>

        <div className="header-buttons">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by account or reference..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <button className="filter-btn" onClick={openFilterModal}>
            <MdFilterList /> Filter
          </button>

          <div className="create-dropdown">
            <button className="create-btn" onClick={toggleCreateDropdown}>
              <MdAdd /> Create New <MdArrowDropDown />
            </button>
            {showCreateDropdown && (
              <div className="create-dropdown-menu">
                <div className="dropdown-item" onClick={() => handleCreateEntry('Payment')}>
                  Payment
                </div>
                <div className="dropdown-item" onClick={() => handleCreateEntry('Invoice')}>
                  Invoice
                </div>
                <div className="dropdown-item" onClick={() => handleCreateEntry('Credit')}>
                  Credit
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Payment Modal */}
      {showPaymentModal && (
        <div className="filter-modal-overlay" onClick={handleCancelPayment}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h2>Create New Payment</h2>
              <button className="close-modal-btn" onClick={handleCancelPayment}>
                <MdClose />
              </button>
            </div>

            <div className="payment-modal-content">
              <h3>Payment Details</h3>

              <div className="payment-field">
                <label>Account</label>
                <select
                  value={paymentForm.account}
                  onChange={(e) => handlePaymentFormChange('account', e.target.value)}
                >
                  <option value="">Select Account</option>
                  {uniqueAccounts.map((account, index) => (
                    <option key={index} value={account}>{account}</option>
                  ))}
                </select>
              </div>

              <div className="payment-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => handlePaymentFormChange('amount', e.target.value)}
                  placeholder="Enter amount"
                />
              </div>

              <div className="payment-field">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => handlePaymentFormChange('paymentDate', e.target.value)}
                />
              </div>

              <div className="payment-field">
                <label>Reference No.</label>
                <input
                  type="text"
                  value={paymentForm.referenceNo}
                  onChange={(e) => handlePaymentFormChange('referenceNo', e.target.value)}
                  placeholder="Enter reference number (optional)"
                />
              </div>

              <div className="payment-field">
                <label>Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => handlePaymentFormChange('paymentMethod', e.target.value)}
                >
                  <option value="">Select Payment Method</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="PayPal">PayPal</option>
                </select>
              </div>
            </div>

            <div className="payment-modal-footer">
              <button className="cancel-payment-btn" onClick={handleCancelPayment}>
                Cancel
              </button>
              <button className="save-payment-btn" onClick={handleSavePayment}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showInvoiceModal && (
        <div className="filter-modal-overlay" onClick={handleCancelInvoice}>
          <div className="invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-modal-header">
              <h2>Invoice Details</h2>
              <button className="close-modal-btn" onClick={handleCancelInvoice}>
                <MdClose />
              </button>
            </div>

            <div className="invoice-modal-content">
              <div className="invoice-field">
                <label>Account</label>
                <select
                  value={invoiceForm.account}
                  onChange={(e) => handleInvoiceFormChange('account', e.target.value)}
                >
                  <option value="">Select Account</option>
                  {uniqueAccounts.map((account, index) => (
                    <option key={index} value={account}>{account}</option>
                  ))}
                </select>
              </div>

              <div className="invoice-field">
                <label>Invoice Date</label>
                <input
                  type="date"
                  value={invoiceForm.invoiceDate}
                  onChange={(e) => handleInvoiceFormChange('invoiceDate', e.target.value)}
                />
              </div>

              <div className="invoice-field">
                <label>Due Date</label>
                <input
                  type="date"
                  value={invoiceForm.dueDate}
                  onChange={(e) => handleInvoiceFormChange('dueDate', e.target.value)}
                  placeholder="Select due date"
                />
              </div>

              <div className="invoice-field">
                <label>Note</label>
                <textarea
                  rows="3"
                  value={invoiceForm.note}
                  onChange={(e) => handleInvoiceFormChange('note', e.target.value)}
                  placeholder="Enter invoice notes here..."
                />
                <span className="note-hint">This note will be visible to customers</span>
              </div>

              <div className="invoice-totals">
                <div className="total-row">
                  <span className="total-label">Subtotal</span>
                  <span className="total-value">£{subtotal.toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Taxes</span>
                  <span className="total-value">£{taxes.toFixed(2)}</span>
                </div>
                <div className="total-row grand-total">
                  <span className="total-label">Total</span>
                  <span className="total-value">£{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="invoice-modal-footer">
              <button className="cancel-invoice-btn" onClick={handleCancelInvoice}>
                Cancel
              </button>
              <button className="save-invoice-btn" onClick={handleSaveInvoice}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Credit Modal */}
      {showCreditModal && (
        <div className="filter-modal-overlay" onClick={handleCancelCredit}>
          <div className="credit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="credit-modal-header">
              <h2>Create New Credit</h2>
              <button className="close-modal-btn" onClick={handleCancelCredit}>
                <MdClose />
              </button>
            </div>

            <div className="credit-modal-content">
              <h3>Credit Details</h3>

              <div className="credit-field">
                <label>Account</label>
                <select
                  value={creditForm.account}
                  onChange={(e) => handleCreditFormChange('account', e.target.value)}
                >
                  <option value="">Select Account</option>
                  {uniqueAccounts.map((account, index) => (
                    <option key={index} value={account}>{account}</option>
                  ))}
                </select>
              </div>

              <div className="credit-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={creditForm.amount}
                  onChange={(e) => handleCreditFormChange('amount', e.target.value)}
                  placeholder="Enter credit amount"
                />
              </div>

              <div className="credit-field">
                <label>Credit Date</label>
                <input
                  type="date"
                  value={creditForm.creditDate}
                  onChange={(e) => handleCreditFormChange('creditDate', e.target.value)}
                />
              </div>

              <div className="credit-field">
                <label>Reference No.</label>
                <input
                  type="text"
                  value={creditForm.referenceNo}
                  onChange={(e) => handleCreditFormChange('referenceNo', e.target.value)}
                  placeholder="Enter reference number (optional)"
                />
              </div>

              <div className="credit-field">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={creditForm.description}
                  onChange={(e) => handleCreditFormChange('description', e.target.value)}
                  placeholder="Enter credit description here..."
                />
              </div>

              <div className="credit-totals">
                <div className="total-row">
                  <span className="total-label">Applied</span>
                  <span className="total-value">£{appliedAmount.toFixed(2)}</span>
                </div>
                <div className="total-row grand-total">
                  <span className="total-label">Unapplied</span>
                  <span className="total-value">£{unappliedAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="credit-modal-footer">
              <button className="cancel-credit-btn" onClick={handleCancelCredit}>
                Cancel
              </button>
              <button className="save-credit-btn" onClick={handleSaveCredit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="filter-modal-overlay" onClick={closeFilterModal}>
          <div className="filter-modal filter-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h2>Filter Entries</h2>
              <button className="close-modal-btn" onClick={closeFilterModal}>
                <MdClose />
              </button>
            </div>

            <div className="filter-modal-content">
              <div className="filter-field">
                <label>Account</label>
                <select
                  value={filterAccount}
                  onChange={(e) => setFilterAccount(e.target.value)}
                >
                  <option value="">Select Account</option>
                  {uniqueAccounts.map((account, index) => (
                    <option key={index} value={account}>{account}</option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label>Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Payment">Payment</option>
                  <option value="Invoice">Invoice</option>
                  <option value="Refund">Refund</option>
                </select>
              </div>

              <div className="filter-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="filter-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="filter-modal-footer">
              <button className="cancel-filter-btn" onClick={closeFilterModal}>
                Cancel
              </button>
              <button className="apply-filter-btn" onClick={handleApplyFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Info */}
      <div className="table-info">
        Showing {totalPostings > 0 ? indexOfFirstPosting + 1 : 0} to {Math.min(indexOfLastPosting, totalPostings)} of {totalPostings} entries
      </div>

      {/* Postings Table */}
      <div className="table-container">
        <table className="postings-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedPostings.length === currentPostings.length && currentPostings.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Post Date</th>
              <th>Type</th>
              <th>Account</th>
              <th>Amount</th>
              <th>Reference</th>
              <th>Statement Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentPostings.map((posting) => (
              <tr key={posting.id} className={selectedPostings.includes(posting.id) ? 'selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedPostings.includes(posting.id)}
                    onChange={() => handleSelectPosting(posting.id)}
                  />
                </td>
                <td>{posting.postDate}</td>
                <td>
                  <span className={`type-badge ${posting.type.toLowerCase()}`}>
                    {posting.type}
                  </span>
                </td>
                <td>{posting.account}</td>
                <td className="amount-cell">{posting.amount}</td>
                <td>{posting.reference}</td>
                <td>{posting.statementDate}</td>
                <td>
                  <div className="action-cell">
                    <button className="view-btn" onClick={() => handleView(posting.id)} title="View">
                      <MdVisibility />
                    </button>
                    <button className="edit-btn" onClick={() => handleEdit(posting.id)} title="Edit">
                      <MdEdit />
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(posting.id)} title="Delete">
                      <MdDelete />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {currentPostings.length === 0 && (
          <div className="postings-empty">
            <FaFileInvoiceDollar className="postings-empty-icon" />
            <p>No postings found</p>
          </div>
        )}
      </div>

      {/* Pagination and Rows Per Page */}
      <div className="pagination-section">
        <div className="rows-per-page">
          <label>Rows per page: </label>
          <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="pagination-info">
          {totalPostings > 0 ? (
            `${indexOfFirstPosting + 1}–${Math.min(indexOfLastPosting, totalPostings)} of ${totalPostings}`
          ) : (
            `0–0 of 0`
          )}
        </div>

        <div className="pagination-controls">
          <button
            className="page-btn"
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
          >
            <FaChevronLeft /> Previous
          </button>
          <span className="current-page">{currentPage}</span>
          <button
            className="page-btn"
            onClick={goToNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Posting;